import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import TableSortLabel from "@mui/material/TableSortLabel";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import TableHead from "@mui/material/TableHead";
import { visuallyHidden } from "@mui/utils";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import API from "../../config/axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { hasAccess, getUserData } from "../../utils/utilities";
import SearchBar from "../../components/SearchBar";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import Button from "../../components/CommonButton";
import CommonDialog from "../../components/CommonDialog";
import FormLabel from "../../components/FormLabel";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function CustomPaginationActionsTable() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(10);
  const [search, setSearch] = React.useState("");
  const [addDialog, setAddDialog] = React.useState(false);
  const [editDialog, setEditDialog] = React.useState(false);
  const [deleteDialog, setDeleteDialog] = React.useState(false);
  const [editToolsId, setEditToolsId] = React.useState(null);
  const [deleteToolsId, setDeleteToolsId] = React.useState(null);
  const [name, setName] = React.useState("");
  const [displayAlert, setDisplayAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertVariant, setAlertVariant] = React.useState("success");
  const [inputErrors, setInputErrors] = React.useState({});
  const [filteredCells, setFilteredCells] = React.useState([]);

  let userData = getUserData();

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = rows.length === 0 ? 1 : 0;

  const displayAlertMessage = (variant, message) => {
    setDisplayAlert(true);
    setAlertMessage(message);
    setAlertVariant(variant);
  };

  const closeAlertMessage = () => {
    setDisplayAlert(false);
    setAlertMessage("");
  };

  const getTools = (searchValue = search) => {
    API.get("admin/tools", {
      params: {
        pagination: true,
        page: page + 1,
        rowsPerPage: rowsPerPage,
        sortBy: orderBy,
        descending: order === "asc" ? false : true,
        filter: searchValue,
      },
    })
      .then((res) => {
        const data = res.data;

        if (data.status) {
          setRows(data.data.results);
          setTotalRows(data.data.totalResults || 0);
          setPage(
            data.data.page > data.data.totalPages ? 0 : data.data.page - 1
          );
        } else {
          console.log("something went wrong");
        }
      })
      .catch((error) => {
        console.log("error", error);
      });
  };

  const addTools = () => {
    API.post("admin/tools", {
      name: name,
    }).then((res) => {
      const data = res.data;

      if (data.status) {
        displayAlertMessage("success", "Tools Added Successfully");
        handleAddDialogClose();
      } else {
        if (data && data.type === "validation") {
          setInputErrors((prevState) => {
            return { [data.error.field]: data.error.message, ...prevState };
          });
        } else {
          displayAlertMessage("error", "Something went wrong");
          handleAddDialogClose();
        }
      }

      getTools();
    });
  };

  const editTools = () => {
    API.put(`admin/tools/${editToolsId}`, {
      name: name,
    })
      .then((res) => {
        const data = res.data;

        if (data.status) {
          displayAlertMessage("success", "Tools Updated Successfully");
          handleEditDialogClose();
          getTools();
        } else {
          if (data && data.type === "validation") {
            setInputErrors((prevState) => {
              return { [data.error.field]: data.error.message, ...prevState };
            });
          } else {
            displayAlertMessage("error", "Something went wrong");
            handleEditDialogClose();
          }
        }
      })
      .catch(() => {
        displayAlertMessage("error", "Something went wrong");
      });
  };

  const deleteTools = () => {
    API.delete(`admin/tools/${deleteToolsId}`)
      .then((res) => {
        const data = res.data;

        if (data.status) {
          displayAlertMessage("success", "Tools Deleted Successfully");
        } else {
          displayAlertMessage("error", data.error || "Something went wrong");
        }
      })
      .catch(() => {
        displayAlertMessage("error", "Something went wrong");
      })
      .finally(() => {
        handleDeleteDialogClose();
        getTools();
      });
  };

  React.useEffect(() => {
    getTools();
    // eslint-disable-next-line
  }, [page, order, orderBy, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddDialogClose = () => {
    setAddDialog(false);
    setInputErrors({});
    setName("");
  };

  const handleEditDialogClose = () => {
    setEditDialog(false);
    setEditToolsId(null);
    setInputErrors({});
    setName("");
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialog(false);
    setDeleteToolsId(null);
  };

  const showEditDialog = (data) => {
    setName(data.name);
    setEditToolsId(data.id || data._id);
    setEditDialog(true);
  };

  const showDeleteConfirmationDialog = (id) => {
    setDeleteToolsId(id);
    setDeleteDialog(true);
  };

  const createSortHandler = (property) => (event) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Tools",
      sortable: true,
    },
    {
      id: "company",
      numeric: false,
      disablePadding: true,
      role: "SuperAdmin",
      label: "Company",
      sortable: true,
    },
    {
      id: "action",
      numeric: false,
      disablePadding: true,
      label: "Actions",
      sortable: false,
    },
  ];

  const getFilteredHeadCells = () => {
    setFilteredCells([]);
    const userRole = userData.role;

    const filteredCells = headCells.filter((cell) => {
      // Hide "actions" column if the user doesn't have access
      if (cell.id === "action" && !hasAccess()) {
        return false;
      }

      // Hide "company" column for non-SuperAdmin users
      if (cell.id === "company" && userRole !== "SuperAdmin") {
        return false;
      }

      // Otherwise, include the column
      return true;
    });
    setFilteredCells(filteredCells);
  };

  React.useEffect(() => {
    getFilteredHeadCells();
  }, []);

  return (
    <div>
      <Grid container rowSpacing={0}>
        <Grid item xs={12} sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: "20px" }} color="#474747">
            Tools Management
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          px={"25px"}
          sx={{ borderRadius: "10px", backgroundColor: "white", py: 3 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="stretch"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 2 }}>
              <SearchBar
                sx={{ width: "100%" }}
                onSearch={(search) => {
                  setSearch(search);
                  getTools(search);
                }}
              />
            </Box>
            <Button onClick={() => setAddDialog(true)}>Add Tools</Button>
          </Stack>

          <TableContainer component={Paper} elevation={0}>
            <Table
              aria-label="custom pagination table"
              className={"custom-table br-0"}
            >
              <TableHead sx={{ backgroundColor: "#f4f6f9" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "80px",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    No.
                  </TableCell>
                  {filteredCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? "right" : "left"}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={createSortHandler(headCell.id)}
                        >
                          {headCell.label}
                          {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell style={{ textAlign: "center" }}>
                      {page * rowsPerPage + (index + 1)}
                    </TableCell>
                    <TableCell size="small" component="th" scope="row">
                      {row.name}
                    </TableCell>
                    {userData.role == "SuperAdmin" && (
                      <TableCell size="small" component="th" scope="row">
                        {row?.companyName || "--"}
                      </TableCell>
                    )}
                    {hasAccess() && (
                      <TableCell
                        size="small"
                        style={{ width: 160 }}
                        align="left"
                      >
                        {(userData.role == "SuperAdmin" ||
                          row.companyId != null) && (
                          <>
                            <IconButton
                              size="small"
                              aria-label="edit"
                              color="inherit"
                              onClick={() => showEditDialog(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="edit"
                              color="inherit"
                              onClick={() =>
                                showDeleteConfirmationDialog(row.id || row._id)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell size="small" colSpan={6}>
                      <Typography sx={{ width: "100%", textAlign: "center" }}>
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[
                      1,
                      5,
                      10,
                      25,
                      { label: "All", value: -1 },
                    ]}
                    colSpan={filteredCells.length + 1}
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <CommonDialog
        open={addDialog || editDialog}
        onClose={addDialog ? handleAddDialogClose : handleEditDialogClose}
        title={`${addDialog ? "Add" : editDialog ? "Update" : ""} Tool`}
        onSubmit={addDialog ? addTools : editTools}
      >
        <Paper elevation={0} sx={{ width: "100%" }}>
          <Box sx={{ padding: "10px" }}>
            <Stack spacing={1}>
              <Grid container>
                <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
                  <FormLabel title="Tool Name" />
                  <TextField
                    id="name"
                    placeholder="Enter Name"
                    value={name}
                    autoFocus
                    size="small"
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    error={inputErrors.name ? true : false}
                    helperText={inputErrors.name || " "}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Paper>
      </CommonDialog>

      <DeleteConfirmationDialog
        open={deleteDialog}
        onClose={handleDeleteDialogClose}
        onDelete={deleteTools}
        name="this tool"
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={displayAlert}
        onClose={closeAlertMessage}
        message={displayAlert}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={alertVariant}>
          {alertMessage}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ ml: 2 }}
            onClick={closeAlertMessage}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
    </div>
  );
}
